import type { TokenSet } from './contract';

/**
 * Rung 1 of the customization ladder: **three designed flavors, each genuinely
 * good** (spec §6.4.3).
 *
 * The word "designed" is load-bearing and is the reason this file is longer than
 * three hex codes. A preset that changes only the accent is a palette swap, and
 * a palette swap does not make a tenant feel custom — it makes three tenants
 * feel like the same site in three shirts, which is exactly the outcome that
 * pushes an owner toward asking for "one small tweak" to the shared bundle. So
 * each preset moves the four things a reader actually perceives:
 *
 *  1. **the type pairing** — what the headline is next to the body;
 *  2. **the rhythm** — how much air, set once by `space.unit` / `space.section`;
 *  3. **the edge** — radius, from a soft 18px to a hard 2px;
 *  4. **the ground** — warm paper, cool paper, or white.
 *
 * Typefaces are stacks of what a browser already has, not webfonts. The bundle
 * is served from a Cloud Storage bucket behind our own LB (§6.4.2) and a font
 * host would be a third-party request on every tenant's booking page — a
 * privacy story to explain and a render-blocking dependency to apologise for.
 * A tenant who wants a licensed face gets it at rung 4 (their stylesheet, pinned
 * to this contract version) or rung 5.
 *
 * The names are how they will be sold: an owner picks a mood, not a token set.
 */

/**
 * **Serene** — the spa. Cool sage over warm stone, wide margins, soft edges, a
 * light old-style serif over a humanist sans. Everything is slightly larger and
 * slightly further apart than it needs to be, because that is what "unhurried"
 * looks like when it is typography rather than a photograph of a candle.
 */
export const SERENE: TokenSet = {
  'color.ink': '#26302b',
  'color.ink-soft': '#59665f',
  'color.ink-faint': '#8d9791',
  'color.paper': '#f6f4ef',
  'color.surface': '#fffdf9',
  'color.surface-alt': '#eeece5',
  'color.line': '#ddd9d0',
  'color.accent': '#5c7f6c',
  'color.accent-ink': '#ffffff',
  'color.accent-soft': '#e6ede8',
  'color.danger': '#94413a',

  'font.display': "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif",
  'font.body': "'Avenir Next', 'Segoe UI', system-ui, -apple-system, sans-serif",
  'font.size-base': '17px',
  'font.size-display': '3rem',
  'font.weight-display': '400',
  'font.leading': '1.65',
  'font.tracking-display': '-0.01em',
  'font.case-display': 'none',

  'radius.sm': '8px',
  'radius.md': '14px',
  'radius.lg': '22px',

  'space.unit': '9px',
  'space.section': '84px',
  'space.measure': '1040px',

  'image.logo': '',
  'image.hero': '',
  'shadow.card': '0 1px 2px rgba(38,48,43,0.04), 0 12px 32px rgba(38,48,43,0.06)',
};

/**
 * **Bistro** — the restaurant. Cream paper, espresso ink, a rust accent, and the
 * only preset that is *tight*: small radii, a compressed spacing unit, a serif
 * body. It reads like a printed menu, which is the point — a bistro's page
 * should feel like something you would find on the table, not like a wellness
 * brochure with different colours.
 */
export const BISTRO: TokenSet = {
  'color.ink': '#2a1c14',
  'color.ink-soft': '#63503f',
  'color.ink-faint': '#9a8878',
  'color.paper': '#fbf4e9',
  'color.surface': '#fffaf1',
  'color.surface-alt': '#f2e7d6',
  'color.line': '#e0d1ba',
  'color.accent': '#a4432b',
  'color.accent-ink': '#fff8f0',
  'color.accent-soft': '#f6e3da',
  'color.danger': '#8c2f22',

  'font.display': "'Hoefler Text', 'Big Caslon', 'Book Antiqua', Georgia, serif",
  'font.body': "'Charter', 'Iowan Old Style', Georgia, 'Times New Roman', serif",
  'font.size-base': '16px',
  'font.size-display': '2.5rem',
  'font.weight-display': '600',
  'font.leading': '1.5',
  'font.tracking-display': '0.005em',
  'font.case-display': 'none',

  'radius.sm': '2px',
  'radius.md': '4px',
  'radius.lg': '6px',

  'space.unit': '7px',
  'space.section': '56px',
  'space.measure': '920px',

  'image.logo': '',
  'image.hero': '',
  'shadow.card': '0 1px 0 rgba(42,28,20,0.08)',
};

/**
 * **Coastal** — the crisp one. White ground, deep slate ink, a saturated marine
 * accent, near-square corners, and a grotesk in both slots with the headline
 * set small, heavy and letterspaced in caps. Where Serene whispers and Bistro
 * is cosy, this is legible at arm's length on a phone in sunlight, which is the
 * brief for a tour operator or a charter.
 */
export const COASTAL: TokenSet = {
  'color.ink': '#0f2436',
  'color.ink-soft': '#4a6072',
  'color.ink-faint': '#8496a4',
  'color.paper': '#ffffff',
  'color.surface': '#ffffff',
  'color.surface-alt': '#eef4f8',
  'color.line': '#d5e0e8',
  'color.accent': '#0e7490',
  'color.accent-ink': '#ffffff',
  'color.accent-soft': '#dff0f5',
  'color.danger': '#a32b2b',

  'font.display': "'Helvetica Neue', Inter, 'Segoe UI', system-ui, Arial, sans-serif",
  'font.body': "'Helvetica Neue', Inter, 'Segoe UI', system-ui, Arial, sans-serif",
  'font.size-base': '16px',
  'font.size-display': '1.9rem',
  'font.weight-display': '700',
  'font.leading': '1.55',
  'font.tracking-display': '0.08em',
  'font.case-display': 'uppercase',

  'radius.sm': '2px',
  'radius.md': '2px',
  'radius.lg': '3px',

  'space.unit': '8px',
  'space.section': '64px',
  'space.measure': '1120px',

  'image.logo': '',
  'image.hero': '',
  'shadow.card': '0 1px 3px rgba(15,36,54,0.10)',
};

export const PRESETS = {
  serene: SERENE,
  bistro: BISTRO,
  coastal: COASTAL,
} as const;

export type PresetName = keyof typeof PRESETS;

/**
 * What a tenant with no branding row gets.
 *
 * `getGuestSite` returns `{}` rather than null for an absent or malformed
 * branding document precisely so "the SPA paints its default preset rather than
 * nothing" (its own comment). This is that preset.
 */
export const DEFAULT_PRESET: PresetName = 'serene';

export const PRESET_NAMES = Object.keys(PRESETS) as PresetName[];

export function isPresetName(name: string): name is PresetName {
  return Object.prototype.hasOwnProperty.call(PRESETS, name);
}
